import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Check, Loader, Sparkles } from 'lucide-react';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { posthog } from 'posthog-js';
import FreeTrial from './FreeTrial';
import { useUserTags } from '../../../hooks/useTags';
import { RootState } from '../../../reducers';
import logger from '../../../lib/logger';
import { requestStatusType } from '../../../types';
import { setupPayments } from '../../../services/subscriptionServices';
import { planPricesPerMonth } from '../../../constants/prices';
import { config } from '../../../../config';
import { createOrUpdateSubscription } from '../thunk';
import { setAlertMessage } from '../../dashboard/dashboardSlice';
import { pushModal } from '../../globalModals/globalModalsSlice';
import { trackStartTrial } from '../../../lib/tracking';

const { stripePublishableKey } = config;
const stripePromise = loadStripe(stripePublishableKey, {
  betas: ['custom_checkout_beta_2'],
});

const getRelativeDate = (daysFromNow: number): string => {
  const date = new Date();
  date.setDate(date.getDate() + daysFromNow);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

interface SignupOnboardingProps {
  onClose: () => void;
}

const LoadingScreen = ({
  recordingSources,
  useCases,
}: {
  recordingSources: Set<string>;
  useCases: Set<string>;
}) => {
  const [messageIndex, setMessageIndex] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);

  // Generate personalized messages based on user selections
  const getPersonalizedMessages = () => {
    const messages = [];

    // First message - always analyzing
    messages.push({ text: 'Analyzing your answers...', icon: Sparkles });

    // Second message - based on recording sources
    if (
      recordingSources.has('Work meetings & calls') ||
      recordingSources.has('School lectures & study sessions')
    ) {
      messages.push({ text: 'Setting up meeting templates...', icon: Check });
    } else if (
      recordingSources.has('Podcasts') ||
      recordingSources.has('YouTube content')
    ) {
      messages.push({ text: 'Preparing content tools...', icon: Check });
    } else {
      messages.push({ text: 'Setting up your workspace...', icon: Check });
    }

    // Third message - based on use cases
    if (
      useCases.has('AI-generated summaries') ||
      useCases.has('Chat with my recordings')
    ) {
      messages.push({ text: 'Customizing your AI assistant...', icon: Check });
    } else if (useCases.has('Searchable transcripts')) {
      messages.push({ text: 'Optimizing search features...', icon: Check });
    } else {
      messages.push({ text: 'Personalizing your experience...', icon: Check });
    }

    // Final message
    messages.push({ text: 'Almost ready...', icon: Check });

    return messages;
  };

  const messages = getPersonalizedMessages();

  useEffect(() => {
    // Cycle through messages
    const intervals = [0, 400, 800, 1200]; // Staggered timings

    intervals.forEach((delay, index) => {
      setTimeout(() => {
        if (index < messages.length) {
          setMessageIndex(index);
          // Mark previous step as completed
          if (index > 0) {
            setCompletedSteps((prev) => [...prev, index - 1]);
          }
        }
      }, delay);
    });
  }, []);

  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center space-y-8 w-full max-w-md">
        {messages.map((message, index) => {
          const Icon = message.icon;
          const isActive = index === messageIndex;
          const isCompleted = completedSteps.includes(index);
          const isVisible = index <= messageIndex;

          let iconColorClass = 'text-gray-400';
          if (isCompleted) {
            iconColorClass = 'text-green-600';
          } else if (isActive) {
            iconColorClass = 'text-blue-600';
          }

          let textColorClass = 'text-muted-foreground/50';
          if (isCompleted) {
            textColorClass = 'text-muted-foreground';
          } else if (isActive) {
            textColorClass = 'text-foreground';
          }

          let iconElement;
          if (isCompleted) {
            iconElement = <Check className="w-6 h-6" />;
          } else if (isActive) {
            iconElement = <Loader className="w-6 h-6 animate-spin" />;
          } else {
            iconElement = <Icon className="w-6 h-6" />;
          }

          return (
            <div
              key={index}
              className={`flex items-center gap-4 transition-all duration-300 ${
                isVisible
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-4'
              }`}
            >
              <div
                className={`flex-shrink-0 transition-all duration-300 ${iconColorClass}`}
              >
                {iconElement}
              </div>
              <div
                className={`text-left text-lg font-medium transition-all duration-300 ${textColorClass}`}
              >
                {message.text}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const TrialPaymentSection = ({
  onClose,
  onboardingData,
}: {
  onClose: () => void;
  onboardingData: {
    howDidYouHear: string | null;
    userType: string | null;
    recordingSources: Set<string>;
    useCases: Set<string>;
  };
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [upgradeStatus, setUpgradeStatus] = useState<requestStatusType>('idle');

  const { team, settings } = useSelector((state: RootState) => state);
  const teamId = team.selectedTeam?.teamId || team.teams[0].teamId;
  const { email } = settings.user;

  const yearlyPrice = planPricesPerMonth.GROWTH.annual * 12;

  const handleError = (error: any) => {
    logger.error(error);
    setUpgradeStatus('failed');
  };

  const handleStartTrial = async () => {
    if (!stripe || !elements) {
      logger.error(t('upgrade.freeTrial.errors.stripeError'));
      return;
    }

    setUpgradeStatus('pending');

    // Track trial CTA tapped
    posthog.capture('onboarding_trial_cta_tapped', {
      product_id: 'growth_annual',
    });

    const { error: submitError } = await elements.submit();
    if (submitError) {
      handleError(submitError);
      return;
    }

    try {
      // Track trial purchase started
      posthog.capture('onboarding_trial_purchase_started', {
        product_id: 'growth_annual',
      });

      const { clientSecret } = await setupPayments({
        teamId,
        metadata: {
          toltReferral: window.tolt_referral,
        },
      });

      const { error } = await stripe.confirmSetup({
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${config.appBaseUrl}`,
        },
        redirect: 'if_required',
      });

      if (error) {
        throw error;
      }

      const result = await dispatch(
        createOrUpdateSubscription({
          teamId,
          plan: 'GROWTH',
          term: 'annual',
          totalAmount: yearlyPrice,
          trialLabel: '3 days free growth annual trial',
          metadata: {
            toltReferral: window.tolt_referral,
          },
        }),
      );

      if (createOrUpdateSubscription.rejected.match(result as never)) {
        onClose();
        const { message = '' } = (result as any).error || {};
        throw new Error(message);
      }

      trackStartTrial({
        plan: 'GROWTH',
        term: 'annual',
        trialLabel: '3 days free growth annual trial',
      });

      // Track trial purchase completed
      posthog.capture('onboarding_trial_purchase_completed', {
        product_id: 'growth_annual',
      });

      // Track onboarding completed with all collected data
      posthog.capture('onboarding_completed', {
        how_did_you_hear: onboardingData.howDidYouHear,
        user_type: onboardingData.userType,
        recording_sources: Array.from(onboardingData.recordingSources),
        recording_sources_count: onboardingData.recordingSources.size,
        use_cases: Array.from(onboardingData.useCases),
        use_cases_count: onboardingData.useCases.size,
      });

      // Mark trial modal as shown to prevent TrialDialogManager from re-opening it
      localStorage.setItem('trialModalShown', 'true');

      setUpgradeStatus('succeeded');

      // Important: onClose() may have animation delays before calling popModal()
      // We need to wait for that to complete before pushing the success modal
      // Otherwise pushModal happens first, then popModal removes it!
      onClose();
      setTimeout(() => {
        dispatch(
          pushModal({
            type: 'upgradeSuccess',
          }),
        );
      }, 300); // Wait for onClose animation + buffer
    } catch (error) {
      setUpgradeStatus('failed');

      // Track trial purchase failed
      posthog.capture('onboarding_trial_purchase_failed', {
        product_id: 'growth_annual',
        error: error.message,
      });

      dispatch(
        setAlertMessage({
          type: 'error',
          message: t('upgrade.freeTrial.errors.upgradeError', {
            message: error.message,
          }),
        }),
      );
      handleError(error);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <PaymentElement
          id="stripe-payment-element"
          options={{
            defaultValues: {
              billingDetails: {
                email,
              },
            },
          }}
        />
      </div>

      <div className="flex items-center justify-center gap-3 py-1">
        <Check className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
        <span className="text-base font-semibold">No payment today</span>
      </div>

      <Button
        type="button"
        variant="default"
        className="w-full h-12 text-base font-bold bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600"
        onClick={handleStartTrial}
        disabled={upgradeStatus === 'pending'}
      >
        {upgradeStatus === 'pending' ? (
          <>
            <Loader className="mr-2 h-5 w-5 animate-spin" />
            {t('upgrade.freeTrial.processing')}
          </>
        ) : (
          t('upgrade.freeTrial.getFreeTrial')
        )}
      </Button>

      <div className="text-center text-sm text-muted-foreground opacity-60">
        Then ${yearlyPrice}/year (${planPricesPerMonth.GROWTH.annual}/mo)
        starting {getRelativeDate(3)}
      </div>
    </div>
  );
};

const SignupOnboarding: React.FC<SignupOnboardingProps> = ({ onClose }) => {
  const { getTag, setTag } = useUserTags();

  // Check if onboarding has already been seen - if so, skip straight to trial
  const hasSeenOnboarding = getTag('onboardingStep') === 'completed';

  const [currentPage, setCurrentPage] = useState(0);
  const [hasNavigated, setHasNavigated] = useState(false);
  const [showStandaloneTrial, setShowStandaloneTrial] = useState(false);
  const showTrial = hasSeenOnboarding;
  const [howDidYouHear, setHowDidYouHear] = useState<string | null>(null);
  const [userType, setUserType] = useState<string | null>(null);
  const [recordingSources, setRecordingSources] = useState<Set<string>>(
    new Set(),
  );
  const [useCases, setUseCases] = useState<Set<string>>(new Set());

  // Track onboarding started
  React.useEffect(() => {
    if (!hasSeenOnboarding) {
      posthog.capture('onboarding_started', {
        onboarding_dismissable: true,
      });
    }
  }, [hasSeenOnboarding]);

  const howDidYouHearOptions = [
    'I was desperately searching for this',
    'Someone sent me a ScreenApp link',
    'Saw it on social media',
    'A friend/coworker recommended it',
    'Found it in the App Store',
    'I honestly forgot',
  ];

  const userTypeOptions = [
    'I need to remember what was said',
    'I want to create content faster',
    "I'm drowning in meetings",
    'I need to study more efficiently',
    'I collaborate with my team',
    'Just exploring',
  ];

  const recordingSourcesOptions = [
    'Work meetings & calls',
    'School lectures & study sessions',
    'My ideas & voice notes',
    'Interviews & conversations',
    'Podcasts',
    'YouTube content',
    'Whatever I need',
  ];

  const useCasesOptions = [
    'AI-generated summaries',
    'Searchable transcripts',
    'Automatic note-taking',
    'Chat with my recordings',
    'YouTube video summaries',
    'All of the above',
  ];

  const handleNext = () => {
    if (currentPage === 3) {
      // After last survey question, show "setting up" screen before trial
      setHasNavigated(true);
      setCurrentPage(4);
      // Auto-advance to trial after 1.5 seconds
      setTimeout(() => {
        setCurrentPage(5);
      }, 1500);
    } else if (currentPage < 5) {
      setHasNavigated(true);
      setCurrentPage(currentPage + 1);
    } else {
      // Final page completed - will be handled by trial submission
      setTag({ key: 'onboardingStep', value: 'completed' });
    }
  };

  // Track page views
  React.useEffect(() => {
    const pageNames = [
      'how_did_you_hear',
      'user_type',
      'recording_sources',
      'use_cases',
      'pricing',
    ];
    if (currentPage < pageNames.length) {
      posthog.capture('onboarding_page_viewed', {
        page_index: currentPage,
        page_name: pageNames[currentPage],
      });
    }
  }, [currentPage]);

  const handleHowDidYouHearSelect = (option: string) => {
    setHowDidYouHear(option);
    posthog.capture('onboarding_how_did_you_hear', {
      selection: option,
    });
    handleNext();
  };

  const handleUserTypeSelect = (option: string) => {
    setUserType(option);
    posthog.capture('onboarding_user_type', {
      selection: option,
    });
    handleNext();
  };

  const toggleRecordingSource = (option: string) => {
    const newSources = new Set(recordingSources);
    const isSelected = newSources.has(option);
    if (isSelected) {
      newSources.delete(option);
    } else {
      newSources.add(option);
    }
    setRecordingSources(newSources);

    // Track each toggle
    posthog.capture('onboarding_recording_sources', {
      selection: option,
      selected: !isSelected,
      total_selected: isSelected ? newSources.size : newSources.size,
    });
  };

  const toggleUseCase = (option: string) => {
    const newUseCases = new Set(useCases);
    const isSelected = newUseCases.has(option);
    if (isSelected) {
      newUseCases.delete(option);
    } else {
      newUseCases.add(option);
    }
    setUseCases(newUseCases);

    // Track each toggle
    posthog.capture('onboarding_use_cases', {
      selection: option,
      selected: !isSelected,
      total_selected: isSelected ? newUseCases.size : newUseCases.size,
    });
  };

  const renderPage = () => {
    switch (currentPage) {
      case 0:
        return (
          <div className="space-y-6">
            <DialogTitle className="text-2xl font-bold text-center">
              How did you hear about ScreenApp?
            </DialogTitle>
            <div className="space-y-3">
              {howDidYouHearOptions.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => handleHowDidYouHearSelect(option)}
                  className={`w-full px-6 py-4 rounded-full text-left transition-all active:scale-[0.98] ${
                    howDidYouHear === option
                      ? 'bg-blue-600 text-white animate-in zoom-in-95 duration-100'
                      : 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
            <Button
              onClick={handleNext}
              className="w-full py-6 rounded-full text-base font-semibold"
            >
              Next →
            </Button>
          </div>
        );

      case 1:
        return (
          <div className="space-y-6">
            <DialogTitle className="text-2xl font-bold text-center">
              Which best describes you?
            </DialogTitle>
            <div className="space-y-3">
              {userTypeOptions.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => handleUserTypeSelect(option)}
                  className={`w-full px-6 py-4 rounded-full text-left transition-all active:scale-[0.98] ${
                    userType === option
                      ? 'bg-blue-600 text-white animate-in zoom-in-95 duration-100'
                      : 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
            <Button
              onClick={handleNext}
              className="w-full py-6 rounded-full text-base font-semibold"
            >
              Next →
            </Button>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <DialogTitle className="text-2xl font-bold text-center">
              What will you record most?
            </DialogTitle>
            <div className="space-y-3">
              {recordingSourcesOptions.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => toggleRecordingSource(option)}
                  className={`w-full px-6 py-4 rounded-full text-left flex items-center gap-3 transition-all active:scale-[0.98] ${
                    recordingSources.has(option)
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-transform ${
                      recordingSources.has(option)
                        ? 'border-white bg-white scale-110'
                        : 'border-gray-400'
                    }`}
                  >
                    {recordingSources.has(option) && (
                      <Check className="w-4 h-4 text-blue-600 animate-in zoom-in-50 duration-100" />
                    )}
                  </div>
                  <span className="flex-1">{option}</span>
                </button>
              ))}
            </div>
            <Button
              onClick={handleNext}
              className="w-full py-6 rounded-full text-base font-semibold"
            >
              Almost done →
            </Button>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <DialogTitle className="text-2xl font-bold text-center">
              What would help you most?
            </DialogTitle>
            <p className="text-sm text-muted-foreground text-center -mt-2">
              We&apos;ll personalize your experience based on your selection
            </p>
            <div className="space-y-3">
              {useCasesOptions.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => toggleUseCase(option)}
                  className={`w-full px-6 py-4 rounded-full text-left flex items-center gap-3 transition-all active:scale-[0.98] ${
                    useCases.has(option)
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-transform ${
                      useCases.has(option)
                        ? 'border-white bg-white scale-110'
                        : 'border-gray-400'
                    }`}
                  >
                    {useCases.has(option) && (
                      <Check className="w-4 h-4 text-blue-600 animate-in zoom-in-50 duration-100" />
                    )}
                  </div>
                  <span className="flex-1">{option}</span>
                </button>
              ))}
            </div>
            <Button
              onClick={handleNext}
              className="w-full py-6 rounded-full text-base font-semibold bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600"
            >
              Let&apos;s go! →
            </Button>
          </div>
        );

      case 4:
        // Loading/affirmation screen
        return (
          <LoadingScreen
            recordingSources={recordingSources}
            useCases={useCases}
          />
        );

      case 5:
        // Trial page
        return (
          <div className="space-y-6">
            <DialogTitle className="text-2xl font-bold text-center">
              Start your free trial to continue
            </DialogTitle>
            <p className="text-center text-muted-foreground -mt-2">
              We&apos;ve personalized ScreenApp based on your answers. Unlock
              your customized experience.
            </p>
            <Elements
              stripe={stripePromise}
              options={{
                mode: 'setup',
                currency: 'usd',
                setupFutureUsage: 'off_session',
              }}
            >
              <TrialPaymentSection
                onClose={onClose}
                onboardingData={{
                  howDidYouHear,
                  userType,
                  recordingSources,
                  useCases,
                }}
              />
            </Elements>
          </div>
        );

      default:
        return null;
    }
  };

  // Handle dialog close - mark onboarding as completed if dismissed
  const handleDialogClose = (open: boolean) => {
    if (!open) {
      // Track skip/dismissal
      posthog.capture('onboarding_skipped', {
        current_page: currentPage,
        how_did_you_hear: howDidYouHear,
        user_type: userType,
        recording_sources: Array.from(recordingSources),
        use_cases: Array.from(useCases),
      });

      // Mark onboarding as completed
      setTag({ key: 'onboardingStep', value: 'completed' });

      // If they dismissed before reaching the trial page (page 5), show standalone trial
      // If they're on loading (page 4) or trial page (page 5), they saw/are seeing the offer - just close
      if (currentPage < 4) {
        setShowStandaloneTrial(true);
      } else {
        onClose();
      }
    }
  };

  // If onboarding was already seen, show standalone trial modal
  if (showTrial) {
    return <FreeTrial onClose={onClose} />;
  }

  // If user dismissed onboarding, show standalone trial modal
  if (showStandaloneTrial) {
    return <FreeTrial onClose={onClose} />;
  }

  return (
    <Dialog open onOpenChange={handleDialogClose}>
      <DialogContent
        className="max-w-lg w-full overflow-hidden p-0 gap-0"
        data-testid="signup-onboarding-modal"
      >
        {/* Instagram-style progress bar */}
        <div className="h-1 bg-gray-200 dark:bg-gray-800 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-blue-600 to-blue-500 transition-all duration-300 ease-out"
            style={{
              width: `${((currentPage + 1) / 6) * 100}%`,
            }}
          />
        </div>

        {/* Content Section with slide animation */}
        <div className="relative overflow-hidden px-6 pb-6 pt-4">
          <div
            key={currentPage}
            className={
              hasNavigated
                ? 'animate-in slide-in-from-right duration-200 fade-in'
                : ''
            }
          >
            {renderPage()}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SignupOnboarding;
